'use client';

import React, { useState } from "react";
import { api, errText } from "./api";
import { AField, AInput, AError, ABtn, ALink, DevCodeHint, StepTag } from "./authUi";

export const ForgotPinFlow = ({ onDone, onBack }) => {
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [devCode, setDevCode] = useState(null);
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async (e) => {
    e?.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/pin/forgot", { phone });
      setDevCode(data.dev_code || null);
      setStep(2);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = async (e) => {
    e.preventDefault();
    if (pin !== pin2) return setError("The two PINs don't match.");
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/pin/reset", { phone, code, new_pin: pin });
      onDone();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  if (step === 1) {
    return (
      <form onSubmit={send} className="space-y-4">
        <div className="flex items-baseline justify-between">
          <h2 className="text-xl font-semibold tracking-tight text-zinc-900">Forgot your PIN?</h2>
          <StepTag step={1} total={2} />
        </div>
        <p className="text-sm text-zinc-500">We'll send a code to your registered mobile number so you can set a new PIN.</p>
        <AError>{error}</AError>
        <AField label="Mobile number">
          <AInput data-testid="forgot-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="98xxxxxxxx" inputMode="numeric" />
        </AField>
        <ABtn data-testid="forgot-send-button" type="submit" busy={busy}>Send me the code</ABtn>
        <div className="pt-1 text-center">
          <ALink data-testid="forgot-back-link" onClick={onBack}>Back to login</ALink>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={reset} className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-xl font-semibold tracking-tight text-zinc-900">Set a new PIN</h2>
        <StepTag step={2} total={2} />
      </div>
      <DevCodeHint code={devCode} />
      <AError>{error}</AError>
      <AField label="6-digit code from SMS">
        <AInput data-testid="forgot-otp-input" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" inputMode="numeric" className="text-center text-lg tracking-[0.5em]" />
      </AField>
      <AField label="New PIN (4–6 digits)">
        <AInput data-testid="forgot-new-pin-input" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} type="password" inputMode="numeric" placeholder="••••" />
      </AField>
      <AField label="Type it again">
        <AInput data-testid="forgot-confirm-pin-input" value={pin2} onChange={(e) => setPin2(e.target.value.replace(/\D/g, "").slice(0, 6))} type="password" inputMode="numeric" placeholder="••••" />
      </AField>
      <ABtn data-testid="forgot-reset-button" type="submit" busy={busy} disabled={code.length !== 6 || pin.length < 4}>Save my new PIN</ABtn>
      <div className="pt-1 text-center">
        <ALink data-testid="forgot-resend-button" onClick={() => send()}>Resend code</ALink>
      </div>
    </form>
  );
};
